import { useCallback } from 'react';

import { useMutation } from '@apollo/client';

import { VALIDATION_TYPES_ARRAY } from 'UI/constants/validationTypes';
import { GET_ALL_ASSIGNED_SKILL_VALIDATIONS } from 'UI/containers/SkillValidation/AllAssignedSkills/getAllAssignedSkillValidations.query';
import { useMessageContext } from 'UI/contexts';
import updateQueryCache from 'UI/customHooks/useUpdateQueryCache';
import { clientLogger } from 'UI/lib';

import gql from 'graphql-tag';

const useDeleteAllAssignedSkillValidations = ({ skillsPoliciesPreset }) => {
  const message = useMessageContext();

  const [deleteSkillValidations] = useMutation(
    gql`
      mutation DELETE_ALL_ASSIGNED_SKILLS($skillIds: [ID]) {
        userValidateSkillRemoveBySkillIds(skillIds: $skillIds)
      }
    `,
    {
      onError: error => {
        clientLogger.error('useDeleteAllAssignedSkillValidations: Error occurred', error);
        message.setMessage({
          text: t => t('restrict:default_error_message'),
        });
      },
    }
  );

  const deleteSkillValidationsCallback = useCallback(
    ({ skillIdsToDelete, shouldGetViewerSkills, validationType }) => {
      return deleteSkillValidations({
        variables: { skillIds: skillIdsToDelete },
        optimisticResponse: { userValidateSkillRemoveBySkillIds: true },
        ...(shouldGetViewerSkills && { refetchQueries: ['GET_VIEWER_SKILLS'] }),
        update: cache => {
          updateQueryCache({
            cache,
            query: GET_ALL_ASSIGNED_SKILL_VALIDATIONS,
            variables: {
              suggestedAction: undefined,
              policiesPreset: { preset: skillsPoliciesPreset },
            },
            update: oldSkillsToValidate => {
              if (validationType) {
                const newSkillsToValidate = oldSkillsToValidate[validationType].filter(
                  oldSkillToValidate => !skillIdsToDelete.includes(oldSkillToValidate.skillId)
                );
                return {
                  ...oldSkillsToValidate,
                  [validationType]: newSkillsToValidate,
                };
              }
              const filteredSkills = VALIDATION_TYPES_ARRAY.reduce((acc, prop) => {
                acc[prop] = oldSkillsToValidate[prop]?.filter(
                  item => !skillIdsToDelete.includes(item.skillId)
                );
                return acc;
              }, {});

              return {
                ...oldSkillsToValidate,
                ...filteredSkills,
              };
            },
          });
        },
      });
    },
    [deleteSkillValidations, skillsPoliciesPreset]
  );

  return {
    deleteSkillValidations: deleteSkillValidationsCallback,
  };
};

export default useDeleteAllAssignedSkillValidations;
